import type { ButtonHTMLAttributes, CSSProperties, HTMLAttributes } from 'react';

import { Icon, type IconName } from './Icon';

export type ModeBadgeSize = 'sm' | 'md';

interface ModeBadgeSharedProps {
  readonly label?: string | undefined;
  readonly prefix?: string;
  readonly icon?: IconName;
  readonly size?: ModeBadgeSize;
  readonly style?: CSSProperties;
}

export interface ModeBadgeProps
  extends ModeBadgeSharedProps,
    Omit<HTMLAttributes<HTMLSpanElement>, 'style' | 'prefix'> {
  readonly onClick?: undefined;
}

export interface ModeBadgeButtonProps
  extends ModeBadgeSharedProps,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style' | 'onClick' | 'prefix'> {
  /** Présence de `onClick` : la pastille devient elle-même le contrôle interactif (remplace un bouton séparé). */
  readonly onClick: () => void;
  readonly expanded?: boolean | undefined;
  /** `aria-controls` du panneau ouvert par cette pastille (sélecteur de mode/état). */
  readonly controls?: string | undefined;
  readonly pending?: boolean | undefined;
}

const badgeStyle = (size: ModeBadgeSize, interactive: boolean, style?: CSSProperties): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  minHeight: size === 'sm' ? 30 : 38,
  padding: `6px ${size === 'sm' ? 14 : 18}px`,
  border: '1px solid var(--accent-line)',
  borderRadius: 'var(--radius-badge)',
  color: 'var(--accent)',
  fontFamily: 'var(--font-ui)',
  fontSize: size === 'sm' ? 10 : 11,
  fontWeight: 500,
  letterSpacing: 'var(--tracking-label)',
  textTransform: 'uppercase',
  background: 'rgba(8,9,10,.42)',
  backdropFilter: 'blur(6px)',
  lineHeight: 1.25,
  maxWidth: '100%',
  /* `font: 'inherit'` a été retiré ici (revue « remplacement image Sport + typo navigation ») :
     ce raccourci CSS, appliqué après fontFamily/fontSize/fontWeight/lineHeight ci-dessus, les
     écrasait silencieusement en `inherit` sur la variante interactive (bouton) — les pastilles
     MODE/ÉTAT réellement affichées à l'écran (toujours interactives dans TodayScreen) héritaient
     donc de la taille du body (--fs-body, 15px) au lieu des 11px voulus par le design system. */
  ...(interactive ? { cursor: 'pointer', transition: 'var(--t-hover)', textAlign: 'left' } : {}),
  ...style,
});

/**
 * Pastille : « ÉTAT — JOIE », « MODE — ÉLÉVATION ». Icône + libellé capitales. Avec `onClick`,
 * devient elle-même le contrôle interactif qui ouvre le sélecteur correspondant — remplace un
 * bouton « Changer de mode »/« Déclarer un état » séparé plutôt que de le dupliquer.
 */
export function ModeBadge(props: ModeBadgeProps | ModeBadgeButtonProps) {
  const { label, prefix = 'ÉTAT', icon = 'joie', size = 'md', style, ...rest } = props;
  const content = (
    <>
      <Icon name={icon} size={size === 'sm' ? 14 : 16} />
      {prefix} — {label}
    </>
  );

  if ('onClick' in props && props.onClick) {
    const { onClick, expanded, controls, pending, disabled, ...buttonRest } =
      rest as Omit<ModeBadgeButtonProps, 'label' | 'prefix' | 'icon' | 'size' | 'style'>;
    return (
      <button
        {...buttonRest}
        type="button"
        onClick={onClick}
        disabled={disabled || pending}
        aria-expanded={expanded}
        aria-controls={controls}
        aria-busy={pending || undefined}
        className="ai-focusable ai-badge-btn"
        style={{ ...badgeStyle(size, true, style), opacity: disabled || pending ? 0.6 : 1 }}
      >
        {content}
      </button>
    );
  }

  return (
    <span {...rest} style={badgeStyle(size, false, style)}>
      {content}
    </span>
  );
}
